import { useListStreamsQuery } from "@/core/cameraApi";
import { intlLocale, tKey, useLocale, type I18nKey } from "@/core/i18n";
import { cn } from "@/lib/utils";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { RotateCcw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useVideoCapture } from "../../context/VideoCaptureContext";
import type {
  CameraRestartScope,
  CameraRestartState,
} from "../../hooks/useCameraRestart";

const SCOPE_OPTIONS: {
  value: CameraRestartScope;
  title: I18nKey<"capture">;
  description: I18nKey<"capture">;
}[] = [
  {
    value: "service",
    title: tKey("capture:restart.scope.service.title"),
    description: tKey("capture:restart.scope.service.description"),
  },
  {
    value: "device",
    title: tKey("capture:restart.scope.device.title"),
    description: tKey("capture:restart.scope.device.description"),
  },
];

export interface RestartCameraButtonProps {
  mxid: string;
  restart: CameraRestartState;
  isIntervalCapturing?: boolean;
}

export const RestartCameraButton = ({
  mxid,
  restart,
  isIntervalCapturing = false,
}: RestartCameraButtonProps) => {
  const { t } = useTranslation(["capture", "common"]);
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<CameraRestartScope>("service");
  const { isRecording } = useVideoCapture();
  const { data: streams } = useListStreamsQuery(mxid);

  const isReboot = scope === "device";
  const streamNames = streams?.map((stream) => stream.name) ?? [];
  // Locale-aware list: "a, b and c" in en-GB, "a, b a c" in cs.
  const streamList = new Intl.ListFormat(intlLocale[locale], {
    type: "conjunction",
  }).format(streamNames);

  const handleConfirm = () => {
    // Closed before the request: the sidebar button and — for a reboot — the
    // overlay on the preview carry the progress from here on.
    setOpen(false);
    void restart.restart(scope);
  };

  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="destructive"
        size="lg"
        className="w-full"
        disabled={restart.isBusy}
        onClick={() => {
          setScope("service");
          setOpen(true);
        }}
      >
        <RotateCcw className="size-4" />
        {restart.isBusy ? t("restart.restarting") : t("restart.title")}
      </Button>

      {restart.isRestartingService && (
        <span className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Spinner />
          {t("restart.reconnecting")}
        </span>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xs font-semibold uppercase tracking-wide">
              {t("restart.title")}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t("restart.dialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            {SCOPE_OPTIONS.map((option) => {
              const isSelected = option.value === scope;
              const isDanger = option.value === "device";
              return (
                <label
                  key={option.value}
                  className={cn(
                    "flex cursor-pointer gap-3 rounded-xl p-3.5 ring-1 transition-colors",
                    !isSelected && "ring-black/10 hover:bg-black/[0.02]",
                    isSelected &&
                      (isDanger
                        ? "bg-destructive/5 ring-destructive"
                        : "bg-primary/5 ring-primary"),
                  )}
                >
                  <input
                    type="radio"
                    name="restart-scope"
                    value={option.value}
                    checked={isSelected}
                    onChange={() => setScope(option.value)}
                    className={cn(
                      "mt-1 size-4 shrink-0",
                      isDanger ? "accent-destructive" : "accent-primary",
                    )}
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="font-medium">{t(option.title)}</span>
                    <span className="text-xs text-muted-foreground">
                      {t(option.description)}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          {isReboot && (
            <div className="flex flex-col gap-1.5 rounded-lg bg-destructive/10 p-3 text-xs text-destructive">
              <span>
                {streamNames.length > 0
                  ? t("restart.warnings.streamsOfflineList", {
                      streams: streamList,
                    })
                  : t("restart.warnings.streamsOffline")}
              </span>
              <span>{t("restart.warnings.captureStops")}</span>
              {isRecording && (
                <span>{t("restart.warnings.recordingDiscarded")}</span>
              )}
            </div>
          )}

          {!isReboot && (isRecording || isIntervalCapturing) && (
            <div className="flex flex-col gap-1.5 rounded-lg bg-black/[0.04] p-3 text-xs text-muted-foreground">
              {isIntervalCapturing && (
                <span>{t("restart.warnings.intervalStops")}</span>
              )}
              {isRecording && (
                <span>{t("restart.warnings.recordingDiscarded")}</span>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t("common:actions.cancel")}
            </Button>
            <Button
              variant={isReboot ? "destructive" : "default"}
              onClick={handleConfirm}
            >
              {isReboot ? t("restart.confirmReboot") : t("restart.confirmRestart")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
